import type { PayLinkRow } from "@/domain/payLinks/payLinkRow";

export const PAY_LINKS_CSV_HEADER =
  "invoice_id,invoice_number,customer_id,customer_name,customer_email,amount_minor,currency,status,created_at,due_date,hosted_url";

export function payLinksCsvFileName(date = new Date()): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `pay-links-${y}-${m}-${d}.csv`;
}

export function buildPayLinksCsv(rows: PayLinkRow[]): string {
  const lines = rows
    .filter((r) => r.hosted_url?.trim())
    .map((r) =>
      [
        csvEscape(r.invoice_id),
        csvEscape(r.invoice_number),
        csvEscape(r.customer_id),
        csvEscape(r.customer_name),
        csvEscape(r.customer_email ?? ""),
        String(r.amount),
        csvEscape(r.currency),
        csvEscape(r.status),
        csvEscape(formatEpochSecondsUtc(r.created)),
        csvEscape(formatEpochSecondsUtc(r.due_date)),
        csvEscape(r.hosted_url),
      ].join(","),
    );
  return [PAY_LINKS_CSV_HEADER, ...lines].join("\n");
}

export function downloadPayLinksCsv(
  rows: PayLinkRow[],
  filename = payLinksCsvFileName(),
) {
  const csv = buildPayLinksCsv(rows);
  triggerCsvDownload(csv, filename);
}

export function triggerCsvDownload(content: string | ArrayBuffer, filename: string) {
  const blob =
    content instanceof ArrayBuffer
      ? new Blob([content], { type: "text/csv;charset=utf-8" })
      : new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function parseContentDispositionFilename(
  contentDisposition: string | undefined,
): string | null {
  if (!contentDisposition) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      return utf8[1].trim();
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(contentDisposition);
  if (quoted?.[1]) return quoted[1];
  const bare = /filename=([^;]+)/i.exec(contentDisposition);
  if (bare?.[1]) return bare[1].trim();
  return null;
}

function formatEpochSecondsUtc(epochSeconds: number | null | undefined): string {
  if (epochSeconds == null) return "";
  return new Date(epochSeconds * 1000).toISOString().replace(/\.000Z$/, "Z");
}

function csvEscape(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
